import React, { useState } from 'react';
import {
  FaGuitar, FaSearch, FaUniversalAccess, FaLayerGroup,
  FaUsersCog, FaMobileAlt, FaLock, FaArrowRight
} from 'react-icons/fa';
import { appUrl, CONTACT_EMAIL, VENTAS_EMAIL } from '../config/host';
import { stripChords } from '../utils/songText';
import './Landing.css';

const EJEMPLO = [
  'C     G        Am',
  'Suena otra vez esa canción',
  '     F        C',
  'que ya nos sabemos de memoria'
].join('\n');

const CARACTERISTICAS = [
  {
    icono: <FaGuitar />,
    titulo: 'Acordes que se ocultan',
    texto:
      'Un interruptor cambia entre la canción con acordes, para quien toca, y solo la letra, para quien canta.'
  },
  {
    icono: <FaUniversalAccess />,
    titulo: 'Legible en el escenario',
    texto:
      'Tamaño de letra ajustable, alto contraste y acordes que nunca se descuadran de la sílaba que les toca.'
  },
  {
    icono: <FaSearch />,
    titulo: 'Búsqueda por la letra',
    texto:
      'Busca por título, autor, número o por una frase que recuerdes. Si sabes cómo dice, la encuentras.'
  },
  {
    icono: <FaLayerGroup />,
    titulo: 'Ordenado por secciones',
    texto:
      'Estrofa, coro, pre-coro, puente, intro y final, cada una con su tonalidad, su tempo y sus etiquetas.'
  },
  {
    icono: <FaUsersCog />,
    titulo: 'Roles para cada quien',
    texto:
      'Administradores que gestionan, editores que cargan canciones y miembros que solo consultan.'
  },
  {
    icono: <FaLock />,
    titulo: 'Cada grupo, lo suyo',
    texto:
      'El repertorio de tu grupo es tuyo y solo lo ven sus integrantes. Nada se mezcla entre grupos.'
  }
];

const Landing: React.FC = () => {
  const [conAcordes, setConAcordes] = useState(true);
  const anio = new Date().getFullYear();

  return (
    <div className="landing">
      {/* ─── Barra ─────────────────────────────────────────── */}
      <nav className="landing-nav">
        <div className="landing-contenedor landing-nav-fila">
          <a className="landing-marca" href="/">
            <img src="/logo.svg" alt="" width={36} height={36} />
            <span>Mi Ensamble</span>
          </a>
          <a className="btn btn-primary" href={appUrl()}>
            Entrar
          </a>
        </div>
      </nav>

      {/* ─── Portada ───────────────────────────────────────── */}
      <header className="landing-hero">
        <div className="landing-contenedor landing-hero-grid">
          <div>
            <h1 className="landing-titulo">El cancionero de tu grupo</h1>
            <p className="landing-bajada">
              Reúne el repertorio de tu banda, tu ensamble o tu coro con la letra y los
              acordes de cada tema, y tenlo listo cuando toca: en la computadora, en la
              tablet del atril o en el teléfono.
            </p>
            <div className="landing-acciones">
              <a className="btn btn-primary btn-lg" href={appUrl()}>
                Entrar al cancionero <FaArrowRight className="ms-2" aria-hidden />
              </a>
              <a
                className="btn btn-outline-light btn-lg"
                href={`mailto:${CONTACT_EMAIL}?subject=Quiero%20Mi%20Ensamble%20para%20mi%20grupo`}
              >
                Solicitar una cuenta
              </a>
            </div>
            <p className="landing-nota">
              Bandas, ensambles, coros, grupos de alabanza, tríos, orquestas. Cada grupo
              recibe su propio espacio y sus cuentas.
            </p>
          </div>

          {/* Demostración en vivo del interruptor de acordes */}
          <div className="landing-demo">
            <div className="landing-demo-barra">
              <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="demo-acordes"
                  checked={conAcordes}
                  onChange={(e) => setConAcordes(e.target.checked)}
                  style={{ width: '2.9rem', height: '1.5rem', cursor: 'pointer' }}
                />
                <label className="form-check-label fw-semibold mb-0" htmlFor="demo-acordes">
                  Mostrar acordes
                </label>
              </div>
              <span className="landing-demo-tono">Tono C</span>
            </div>

            <div className="landing-demo-cuerpo">
              <div className="landing-demo-etiqueta">Estrofa 1</div>
              <pre className="landing-demo-texto">
                {conAcordes ? EJEMPLO : stripChords(EJEMPLO)}
              </pre>
            </div>

            <p className="landing-demo-pie">
              Pruébalo: es exactamente lo que verá tu equipo.
            </p>
          </div>
        </div>
      </header>

      {/* ─── Características ───────────────────────────────── */}
      <section className="landing-seccion">
        <div className="landing-contenedor">
          <h2 className="landing-h2">Pensado para el ensayo y para el escenario</h2>
          <div className="row g-4 mt-1">
            {CARACTERISTICAS.map((c) => (
              <div className="col-md-6 col-lg-4" key={c.titulo}>
                <div className="landing-tarjeta h-100">
                  <div className="landing-icono" aria-hidden>{c.icono}</div>
                  <h3 className="landing-h3">{c.titulo}</h3>
                  <p className="mb-0 text-secondary">{c.texto}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Cómo se usa ───────────────────────────────────── */}
      <section className="landing-seccion landing-seccion-clara">
        <div className="landing-contenedor">
          <h2 className="landing-h2">Cómo se usa</h2>
          <div className="row g-4 mt-1">
            {[
              ['1', 'Carga el repertorio', 'Desde la computadora, pega una canción completa desde donde la tengas y el editor la separa en secciones solo.'],
              ['2', 'Reparte los accesos', 'Das de alta a tu grupo con el rol que corresponda: quién puede editar y quién solo consultar.'],
              ['3', 'Canta y toca', 'En el ensayo o en vivo, cada quien abre la canción y elige si quiere ver los acordes o solo la letra.']
            ].map(([n, titulo, texto]) => (
              <div className="col-md-4" key={n}>
                <div className="landing-paso">
                  <span className="landing-paso-num">{n}</span>
                  <h3 className="landing-h3">{titulo}</h3>
                  <p className="mb-0 text-secondary">{texto}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Móvil ─────────────────────────────────────────── */}
      <section className="landing-seccion">
        <div className="landing-contenedor landing-movil">
          <div className="landing-icono-grande" aria-hidden><FaMobileAlt /></div>
          <div>
            <h2 className="landing-h2 mb-2">También en el teléfono</h2>
            <p className="text-secondary mb-0">
              La aplicación para Android trae el mismo cancionero, con la letra grande y el
              modo de alto contraste para leerla bajo las luces del escenario.
              <strong> Próximamente en Google Play.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ─── Probar sin registrarse ────────────────────────── */}
      <section className="landing-seccion landing-seccion-clara">
        <div className="landing-contenedor text-center">
          <h2 className="landing-h2">Pruébalo ahora mismo</h2>
          <p className="landing-parrafo mx-auto" style={{ maxWidth: '34rem' }}>
            Hay una cuenta de demostración abierta. No hay que registrarse ni dar
            ningún dato: se entra, se mira todo y ya.
          </p>
          <a className="btn btn-primary btn-lg" href="/demo">
            Ver el demo
          </a>
        </div>
      </section>

      {/* ─── Solicitar cuenta / apoyar ─────────────────────────
          OJO: esta sección NO debe enlazarse desde la aplicación Android. La
          política de pagos de Google prohíbe que una app dirija a pagar por
          fuera, y eso incluye enlazar a una página que lo ofrece. Por eso el
          botón del APK apunta a /demo, que no tiene nada de esto. */}
      <section id="solicitar" className="landing-cierre">
        <div className="landing-contenedor">
          <div className="text-center mb-4">
            <h2 className="landing-h2 text-white">¿Lo quieres para tu grupo?</h2>
            <p className="text-white-50 mb-0">
              Te abrimos el espacio de tu grupo con tus canciones y tus usuarios.
            </p>
          </div>

          <div className="landing-solicitar">
            <div className="landing-solicitar-caja">
              <h3 className="landing-h3">Escríbenos</h3>
              <p className="mb-3">
                Cuéntanos el nombre de tu grupo y cuántas personas son. Te respondemos
                con los pasos.
              </p>
              <a
                className="btn btn-light btn-lg"
                href={`mailto:${VENTAS_EMAIL}?subject=Quiero%20Mi%20Ensamble%20para%20mi%20grupo`}
              >
                {VENTAS_EMAIL}
              </a>
            </div>

            <div className="landing-solicitar-caja">
              <h3 className="landing-h3">Yape</h3>
              <p className="mb-3">
                Si estás en Perú, puedes pagar o apoyar el proyecto con una donación
                desde la aplicación Yape.
              </p>
              {/* La imagen la pone el dueño del proyecto en public/yape.png.
                  Si no está, no se enseña un recuadro roto. */}
              <img
                className="landing-yape"
                src="/yape.png"
                alt="Código QR de Yape para pagos y donaciones"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
              <p className="landing-yape-pie">
                Escanea desde Yape. Después escríbenos al correo para activar tu cuenta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pie ───────────────────────────────────────────── */}
      <footer className="landing-pie">
        <div className="landing-contenedor landing-pie-fila">
          <span>© {anio} Mi Ensamble · R&amp;P Solutions</span>
          <span className="landing-pie-enlaces">
            <a href="/privacidad.html">Privacidad</a>
            <a href="/demo">Demo</a>
            <a href={`mailto:${VENTAS_EMAIL}`}>{VENTAS_EMAIL}</a>
            <a href={appUrl()}>Entrar</a>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
